import React, { useMemo, useState, useCallback } from 'react';
import DonutChart from 'components/DashboardNew/widgets/Charts/DonutChart';
import BarChart from 'components/DashboardNew/widgets/Charts/BarChart';
import LineChart from 'components/DashboardNew/widgets/Charts/LineChart';

function pickChartComponent(chartType) {
    if (chartType === 'bar-chart') return BarChart;
    if (chartType === 'line-chart') return LineChart;
    return DonutChart; // default
}

export default function ChartListStatCard({ items = [], rows = [], xKey, yAxisKeys = [], theme = {}, dataContext }) {
    const {
        list = {},
        chart = {},
    } = theme || {};

    const {
        columns = 2,
        showBars = true,
        visibility = { label: true, subLabel: true, users: true, split: true, percent: true },
        colors = ['#22c55e', '#8b5cf6', '#06b6d4', '#ef4444', '#f59e0b', '#0ea5e9', '#14b8a6', '#6366f1'],
        trackColor = 'rgba(148, 163, 184, 0.25)',
        valueClass = 'text-base font-semibold text-[#0F172A]',
        labelClass = 'text-[#0f172acc] text-base font-medium',
        subLabelKey,
        subLabelClass = 'text-[12px] text-[#6B7280] font-normal',
        barHeight = 6,
        maxItems = 12,
        divider = { visible: true, color: '#E5E7EB', width: 1 },
    } = list || {};

    const chartType = chart?.type || theme?.chartType || 'donut-chart';
    const ChartComp = pickChartComponent(chartType);
    const chartTheme = chart?.theme || chart;
    const chartThemeEffective = useMemo(() => {
        // Ensure donut uses the same palette as the left list when chart theme doesn't specify colors
        if (chartType === 'donut-chart') {
            if (!chartTheme?.colors && Array.isArray(colors) && colors.length) {
                return { ...(chartTheme || {}), colors };
            }
        }
        return chartTheme || {};
    }, [chartTheme, colors, chartType]);

    const [legendItems, setLegendItems] = useState([]);
    const areLegendItemsEqual = useCallback((a, b) => {
        if (a === b) return true;
        if (!Array.isArray(a) || !Array.isArray(b)) return false;
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            const ai = a[i] || {};
            const bi = b[i] || {};
            if (ai.label !== bi.label || ai.color !== bi.color) return false;
        }
        return true;
    }, []);
    const handleExposeLegend = useCallback((items) => {
        if (!Array.isArray(items)) return;
        setLegendItems(prev => (areLegendItemsEqual(prev, items) ? prev : items));
    }, [areLegendItemsEqual]);

    const legendColorByLabel = useMemo(() => {
        const map = Object.create(null);
        if (Array.isArray(legendItems)) {
            legendItems.forEach(it => {
                if (it && typeof it.label !== 'undefined') {
                    map[String(it.label)] = it.color;
                }
            });
        }
        return map;
    }, [legendItems]);

        const limitedItems = useMemo(() => Array.isArray(items) ? items.slice(0, maxItems) : [], [items, maxItems]);
    const maxValue = useMemo(() => {
        if (!limitedItems.length) return 0;
            return limitedItems.reduce((m, i) => Math.max(m, Number(i?.percentValue ?? i?.value ?? 0)), 0);
    }, [limitedItems]);

    const gridColsClass = `grid-cols-${Math.max(1, Math.min(3, Number(columns) || 2))}`;

    // Allow nested chart to override x/y keys, e.g. donut uses { xAxisKey: 'package_name', yAxisKey: 'total_users' }
    const chartXKey = chartThemeEffective?.xAxisKey || xKey;
    const chartYKeys = Array.isArray(chartThemeEffective?.yAxisKeys)
        ? chartThemeEffective.yAxisKeys
        : (chartThemeEffective?.yAxisKey ? [chartThemeEffective.yAxisKey] : (Array.isArray(yAxisKeys) ? yAxisKeys : []));

    return (
        <div className="flex items-stretch gap-6">
            <div className={`flex-1 relative min-h-[280px]`}>
                <div className={`grid ${gridColsClass} gap-x-10 gap-y-6`}>
                {limitedItems.map((it, idx) => {
                    const labelKey = typeof it?.label !== 'undefined' ? String(it.label) : undefined;
                    const legendColor = (labelKey && legendColorByLabel[labelKey]) || (legendItems && legendItems[idx]?.color);
                    const color = it?.color || legendColor || colors[idx % colors.length];
                    const value = Number(it?.value ?? 0);
                    const pct = it?.percentValue != null
                        ? Math.max(0, Math.min(100, Number(it.percentValue)))
                        : (maxValue > 0 ? Math.max(0, Math.min(100, (value / maxValue) * 100)) : 0);
                    const subText = it?.subLabel ?? (subLabelKey && it && it[subLabelKey] ? String(it[subLabelKey]) : '');
                    return (
                        <div key={`${it?.label ?? idx}`} className="flex items-center gap-5">
                            <div className={`flex flex-col flex-grow`}>
                                {visibility.label ? <div className={labelClass}>{it?.label ?? ''}</div> : null}
                                {visibility.subLabel && subText ? <div className={subLabelClass}>{subText}</div> : null}
                            </div>

                            <div className={`flex flex-col`}>
                                {visibility.users ? <div className={labelClass}>{it?.usersText ?? ''}</div> : null}
                                {visibility.split && it?.splitText ? <div className={subLabelClass}>{it.splitText}</div> : null}
                            </div>

                            <div className={`flex flex-col`}>
                                {visibility.percent ? <div className={labelClass}>{`${pct.toFixed(1)}%`}</div> : null}
                                {showBars ? (
                                    <div className="mt-2 h-2 rounded-full" style={{ backgroundColor: trackColor, height: barHeight }}>
                                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                                    </div>
                                ) : null}
                            </div>

                            {/* <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                    <div className="truncate mr-3">
                                        <div className={labelClass}>{it?.label ?? ''}</div>
                                        {subText ? <div className={subLabelClass}>{subText}</div> : null}
                                    </div>
                                    <div className={`${valueClass} shrink-0`}>{value.toLocaleString()} {valueSuffix}</div>
                                </div>
                                {showBars ? (
                                    <div className="mt-2 h-2 rounded-full" style={{ backgroundColor: trackColor, height: barHeight }}>
                                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                                    </div>
                                ) : null}
                             </div> */}
                        </div>
                    );
                })}
                </div>
                {columns > 1 && divider?.visible !== false ? (
                    <div
                        className="pointer-events-none absolute inset-y-0 left-1/2"
                        style={{ width: divider?.width || 1, backgroundColor: divider?.color || '#E5E7EB' }}
                    />
                ) : null}
            </div>

            <div className="w-[360px] shrink-0">
                <div className="min-h-[280px]">
                    {ChartComp === BarChart ? (
                        <ChartComp
                            data={Array.isArray(rows) && chartXKey && Array.isArray(chartYKeys) && chartYKeys[0]
                                ? rows.map(r => ({ name: r?.[chartXKey], value: r?.[chartYKeys[0]] }))
                                : []}
                            theme={chartThemeEffective}
                            onExposeLegend={handleExposeLegend}
                        />
                    ) : (
                        <ChartComp
                            rows={rows}
                            xKey={chartXKey}
                            yAxisKeys={Array.isArray(chartYKeys) ? chartYKeys : []}
                            theme={chartThemeEffective}
                            dataContext={dataContext}
                            onExposeLegend={handleExposeLegend}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}


